import { ArtistProfileStatus, type WorkItemDto } from '@customTypes/types';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import { useArtistCurrentContractQuery } from '@/features/work/api';
import { useDeleteWorkRequestMutation } from '@/features/work/api/delete-work-request';
import { useShowFullClubProfileQuery } from '@/features/work/api/show-full-club-profile';
import {
  useArtistProfileProgressQuery,
  useArtistProfileStatusQuery
} from '@/shared/api/requests';

import { interactionWorkItemsStatusFilters } from '../../constants/filterStatuses';

export type CantSendRequestCause =
  | 'incompleted'
  | 'have-contract'
  | 'hidden-from-search'
  | 'have-pending-request'
  | 'is-not-hiring'
  | null;

export const useSendWorkRequestView = ({
  artistId,
  workItem,
  clubId
}: {
  artistId: string;
  clubId: string;
  workItem: WorkItemDto | null;
}) => {
  const [cause, setCause] = useState<CantSendRequestCause>(null);

  const queryClient = useQueryClient();
  const router = useRouter();
  const { formatMessage } = useIntl();
  const deleteWorkRequest = useDeleteWorkRequestMutation();
  const artistCompletion = useArtistProfileProgressQuery({
    profileId: artistId
  });
  const currentContract = useArtistCurrentContractQuery({
    profileId: artistId
  });
  const artistProfileStatus = useArtistProfileStatusQuery({
    profileId: artistId
  });

  const clubFullProfile = useShowFullClubProfileQuery({ profileId: clubId });

  useEffect(() => {
    if (
      artistCompletion.data?.data &&
      artistCompletion.data?.data.totalCompletion < 100
    ) {
      return setCause('incompleted');
    }

    if (currentContract.data?.data) {
      return setCause('have-contract');
    }
    if (!clubFullProfile.data?.data.clubInfo.isHiring) {
      return setCause('is-not-hiring');
    }

    if (
      artistProfileStatus.data?.data &&
      artistProfileStatus.data.data.status ===
        ArtistProfileStatus.HiddenFromSearch
    ) {
      return setCause('hidden-from-search');
    }
    setCause(null);
  }, [
    workItem,
    artistCompletion,
    artistProfileStatus,
    clubFullProfile,
    currentContract
  ]);

  const deleteRequest = async () => {
    if (workItem) {
      await deleteWorkRequest.mutateAsync(
        {
          params: {
            workRequestId: workItem.workItemId.toString()
          }
        },
        {
          onSuccess: () => {
            notifications.show({
              title: formatMessage({
                id: 'notifications.workItemIsCanceled'
              }),
              loading: true,
              ...notificationStyleConfig
            });

            queryClient.invalidateQueries({
              queryKey: [
                'ArtistWorkItemsList',
                artistId,
                interactionWorkItemsStatusFilters
              ],
              exact: true
            });

            if (workItem) {
              router.history.back();
            }
          }
        }
      );
    }
  };

  const canSendRequest = workItem == null;

  return {
    state: { canSendRequest, cause },
    functions: { deleteRequest }
  };
};
